import PagePanel from '../../../layout/PagePanel'
import { LocaleContext } from '../../../../providers/locale-provider'
import { RegistrationCheckContext } from '../../../../providers/registration-check-provider'
import { PagePanelColors } from '../../../../types/page-panel'
import { Endpoint, QuestionNotificationTypes } from '../../../../types/questions'
import {
    InfoModal,
    InfoSuccessIcon,
    InfoNoIcon,
    RichText,
    FormDatePicker,
    FormField,
    DictionaryText,
} from '../../../../../MyStichtingOpenComponents/src'
import { ReactElement, useContext, useEffect, useState } from 'react'

const MESSAGE_KEYS = {
    START_DATE: 'STO.CHECK.START_DATE',
}

interface Props {
    endpoint: Endpoint
    activityAndQuestionCombinationKey: string
}

const notificationConfig: Record<
    QuestionNotificationTypes,
    {
        panelColor: PagePanelColors
        icon: ReactElement
        iconColor: string
    }
> = {
    [QuestionNotificationTypes.SUCCESS]: {
        panelColor: PagePanelColors.INFO,
        icon: <InfoSuccessIcon />,
        iconColor: 'text-accent-700',
    },
    [QuestionNotificationTypes.WARNING]: {
        panelColor: PagePanelColors.WARNING,
        icon: <InfoNoIcon />,
        iconColor: 'text-warning-700',
    },
}

export const EndpointNotification = ({ endpoint, activityAndQuestionCombinationKey }: Props): JSX.Element => {
    const localeContext = useContext(LocaleContext)
    const [isInfoModalVisible, setInfoModalVisibility] = useState(false)
    const [startDate, setStartDate] = useState(null)
    const endpointKey = endpoint.endpointKey

    if (!endpoint.result.endpointNotification) return

    const { type, additionalInformation, text } = endpoint.result.endpointNotification

    const { panelColor, icon, iconColor } =
        notificationConfig[type] || notificationConfig[QuestionNotificationTypes.SUCCESS]

    const { endpointsPerFormKey, setEndpointsPerFormKey } = useContext(RegistrationCheckContext)

    const onStartDateChange = (startDate: Date) => {
        const endpoints = endpointsPerFormKey[activityAndQuestionCombinationKey]
        const matchingEndpoint = endpoints.find((entry) => entry.endpointKey === endpoint.endpointKey)

        if (matchingEndpoint) {
            matchingEndpoint.startDate = startDate
            setEndpointsPerFormKey(activityAndQuestionCombinationKey, endpoints)
        }
    }

    useEffect(() => {
        onStartDateChange(startDate)
    }, [startDate])

    return (
        <span data-cy={`question-notification__type-${type}`}>
            <PagePanel
                color={panelColor}
                onClickMoreInfo={additionalInformation ? () => setInfoModalVisibility(true) : undefined}
                containerClasses="flex gap-2 items-center w-full"
            >
                {icon && <div className={`shrink-0 inline-flex -ml-8 ${iconColor}`}>{icon}</div>}
                <div>
                    <RichText rawHtml={localeContext.getLocaleText(text)} />
                    {type === QuestionNotificationTypes.SUCCESS && (
                        <FormField name="date" label={<DictionaryText textKey={MESSAGE_KEYS.START_DATE} />}>
                            <FormDatePicker
                                id={endpointKey}
                                placeholderText="DD/MM/YYYY"
                                dateFormat="dd/MM/yyyy"
                                name={endpointKey}
                                maxDate={new Date()}
                                defaultDate={startDate}
                                onChange={setStartDate}
                            />
                        </FormField>
                    )}
                </div>
            </PagePanel>
            {additionalInformation && (
                <InfoModal
                    isModalVisible={isInfoModalVisible}
                    setModalVisibility={setInfoModalVisibility}
                    title={localeContext.getLocaleText(additionalInformation.title)}
                >
                    <RichText rawHtml={localeContext.getLocaleText(additionalInformation.description)} />
                </InfoModal>
            )}
        </span>
    )
}
